import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { RangeValue } from '@react-types/shared';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import { getHumanReadableStepName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import AnalyticsDatePicker from 'dashboard/components/AnalyticsDatePicker';
import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import DisabledProductFeature from 'dashboard/components/DisabledProductFeature';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import LoadingOverlay from 'dashboard/components/LoadingOverlay';
import NavBack from 'dashboard/components/NavBack';
import ReactSelect from 'dashboard/components/ReactSelect';
import Tooltip from 'dashboard/components/Tooltip';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Backdrop, SoloColumn } from 'dashboard/layouts/ShowViewLayoutV2';
import { Period } from 'dashboard/lib/unixTime';
import { generateDomain, getEffectiveInterval, getPeriod } from 'dashboard/lib/utils/charting';
import {
  formatChartDate,
  formatChartDateHourChart,
  formatChartMonthName,
} from 'dashboard/lib/utils/formatters';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  MetricsTemplateFunnelConversionOverTime,
  MetricsTemplateFunnelFlow,
} from 'dashboard/models/metrics';
import { MetricsFilterParams } from 'dashboard/redux/actions/metrics-inquiry';
import { MetricsState } from 'dashboard/redux/reducers/metrics-inquiry';
import { ABSOLUTE_RELATIVE_OPTIONS } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/DashboardInquiryAnalyticsFunnelV2View';
import FunnelConversionBarChart, {
  BarValue,
} from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/FunnelConversionBarChart';
import FunnelConversionOverTimeLineChart, {
  MetricWithTimestamp,
  TimestampToPercentages,
} from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/FunnelConversionOverTimeLineChart';
import { ConvertToFunnelNode } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelAnalyticsFlowVisualizer';
import { FunnelNode } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelTree';
import FunnelGranularitySelector from 'dashboard/views/InquiryFunnelAnalyticsDetailedView/FunnelGranularitySelector';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import IconInquiries from 'images/icons/inquiries.svg';

export const InquiryFunnelAnalyticsFlowVisualizerContainer = styled.div`
  display: flex;
  height: calc(100vh - 500px);
`;

const StyledLabel = styled(FormControlLabel)`
  margin-left: ${Spacing[2]};
`;

const TooltipContainer = styled('div')`
  max-width: 500px;
`;

export interface InquiryFunnelAnalyticsDetailedViewProps extends Pick<MetricsState, 'filters'> {
  loading: boolean;
  lastQueryFilters: MetricsFilterParams;
  productFeatureIsDisabled: boolean;
  stepIds: string[];
  template?: InquiryTemplate;
  templateFunnelConversionOverTime: MetricsTemplateFunnelConversionOverTime;
  templateFunnelFlow?: MetricsTemplateFunnelFlow;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
  handleUpdateGranularity: (period: Period) => void;
  emptyData: boolean;
  funnelPeriod: Period;
  loadMetrics: ({
    inquiryTemplateId,
    inquiryTemplateVersionId,
  }: {
    inquiryTemplateId: string;
    inquiryTemplateVersionId: string;
  }) => Promise<void>;
}

const Container = styled('div')``;

const Header = styled('div')`
  display: flex;
`;

const NavBackContainer = styled('div')`
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const Section = styled('div')`
  min-height: 480px;
  width: 100%;
`;

const X_AXIS_HEIGHT = 90;
const CARD_PADDING = 80;

const findPathToStep = (funnelNodes: FunnelNode[], stepIds: string[]): FunnelNode[] | null => {
  const path: FunnelNode[] = [];
  for (const step of funnelNodes) {
    if (stepIds.length === 0) {
      return path;
    }
    if (step.token !== stepIds[0]) {
      return null;
    }
    path.push(step);
    stepIds = stepIds.slice(1);
  }

  if (stepIds.length === 0) {
    return path;
  }
  const lastNode = funnelNodes[funnelNodes.length - 1];
  if (lastNode.branches == null || lastNode.branches.length === 0) {
    return null;
  }
  for (const branch of lastNode.branches) {
    const branchSearch = findPathToStep(branch, stepIds);
    if (branchSearch != null) {
      return path.concat(branchSearch);
    }
  }
  return null;
};

const buildFunnelConversionData = (
  funnelNodes: FunnelNode[],
  stepIds: string[],
): { path: BarValue[]; stepTokenToName: Record<string, string> } => {
  const pathToStep = findPathToStep(funnelNodes, stepIds);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const zeroValue = pathToStep.reduce((acc, step) => {
    acc[step.token] = 0;
    return acc;
  }, {} as BarValue);
  const stepTokenToName: Record<string, string> = {};
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const path = pathToStep.map((step) => {
    const value: BarValue = {
      ...zeroValue,
      [step.token]: step.metrics.value,
    };
    value['_id'] = step.token;
    stepTokenToName[step.token] = step.name;
    return value;
  });
  return { path, stepTokenToName };
};

const convertToChartData = (
  templateFunnelConversionOverTime: MetricsTemplateFunnelConversionOverTime,
  isAbsolute: boolean,
): { chartData: MetricWithTimestamp[]; percentagesData: TimestampToPercentages } => {
  const createdAtToData: { [key in number]: MetricWithTimestamp } = {};
  const percentagesData: TimestampToPercentages = {};
  templateFunnelConversionOverTime.data.forEach((data) => {
    (data.data || []).forEach((d) => {
      createdAtToData[d.created_timestamp] = createdAtToData[d.created_timestamp] ?? {
        created_timestamp: d.created_timestamp,
      };
      if (!isAbsolute) {
        createdAtToData[d.created_timestamp][data.id] = d.percentage.relative;
      } else {
        createdAtToData[d.created_timestamp][data.id] = d.percentage.absolute;
      }
      percentagesData[d.created_timestamp] = percentagesData[d.created_timestamp] ?? {};
      percentagesData[d.created_timestamp][data.id] = {
        numerator: d.value,
        denominator: isAbsolute ? d.percentage.absolute_base : d.percentage.relative_base,
      };
    });
  });
  return {
    chartData: Object.values(createdAtToData).sort(
      (data1, data2) => data1.created_timestamp - data2.created_timestamp,
    ),
    percentagesData,
  };
};

const buildConversionOverTimeIdToNameMapping = (data: MetricsTemplateFunnelConversionOverTime) => {
  const base: Record<string, string> = {};
  return data.data.reduce((a, b) => {
    // TODO: [jonny] add back the b.id === b.name ? getHumanReadableStepName(b.name) || b.name
    // This is because our conditions are written uniquely and we'll likely get rid of them in our V2, giving us time to adjust
    a[b.id] = getHumanReadableStepName(b.name);
    return a;
  }, base);
};

export const renderFunnelFlow = (
  templateFunnelFlow: MetricsTemplateFunnelFlow,
  stepIds: string[],
  isAbsolute: boolean,
  compactOptions:
    | undefined
    | {
        height: number;
      },
) => {
  const funnelNodes = ConvertToFunnelNode(templateFunnelFlow);
  const { path, stepTokenToName } = buildFunnelConversionData(funnelNodes, stepIds);
  const funnelNamePrefix = isAbsolute ? 'Overall' : 'Per Step';
  return (
    <Card>
      <Card.Body>
        {!compactOptions ? (
          <Card.Title data-test='inquiry-funnel-funnel-conversion'>
            {funnelNamePrefix} Funnel
          </Card.Title>
        ) : (
          <h4 data-test='inquiry-funnel-funnel-conversion'>{funnelNamePrefix} Funnel</h4>
        )}
        <FunnelConversionBarChart
          data={path}
          seriesFormatter={(id: string) => stepTokenToName[id]}
          stepIds={Object.keys(stepTokenToName)}
          isAbsolute={isAbsolute}
          isCompact={compactOptions != null}
          chartHeight={
            compactOptions == null
              ? undefined
              : compactOptions.height - X_AXIS_HEIGHT - CARD_PADDING
          }
          xAxisHeight={X_AXIS_HEIGHT}
        />
      </Card.Body>
    </Card>
  );
};

const SearchAndFilterCardBody = styled(Card.Body)`
  grid-gap: 0;
  grid-template-columns: 1fr auto;

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const SearchAndFilterContainer = styled.div`
  width: 100%;
  flex: auto;
  display: grid;
  align-items: end;
  grid-template-columns: minmax(300px, 2fr) 2fr 1fr 1fr;
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const DateContainer = styled.div`
  display: grid;
  grid-column: 1;
`;

const StepContainer = styled.div`
  display: grid;
  grid-column: 2;
`;

const ConversionContainer = styled.div`
  display: grid;
  grid-column: 3;
`;

const RunContainer = styled.div`
  display: grid;
  width: 100px;
  grid-column: 4;
  margin-right: 0;
  margin-left: auto;
  justify-items: right;
`;

const RunButton = styled(Button)`
  height: ${Spacing[6]};
  width: ${Spacing[8]};
`;

const StyledSelect = styled(ReactSelect<string>)`
  padding: 0px ${Spacing[2]};
`;

const filterStepIds = (stepIds: string[], stepId: string) => {
  const index = stepIds.indexOf(stepId);
  if (index === -1) {
    return stepIds;
  }
  return stepIds.slice(0, index + 1);
};

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const InquiryFunnelAnalyticsDetailedView = ({
  loading,
  emptyData,
  productFeatureIsDisabled,
  stepIds,
  template,
  templateFunnelConversionOverTime,
  templateFunnelFlow,
  handleDateUpdateV2,
  handleUpdateGranularity,
  funnelPeriod,
  filters,
  lastQueryFilters,
  loadMetrics,
}: InquiryFunnelAnalyticsDetailedViewProps) => {
  const [isAbsolute, setIsAbsolute] = useState(true);
  const [stepId, setStepId] = useState(stepIds[stepIds.length - 1]);
  const filteredStepIds = filterStepIds(stepIds, stepId);
  const { createdAtStart, createdAtEnd } = filters;

  const dateRange = useMemo<AnalyticsDateRange>(() => {
    return {
      startDate: createdAtStart ? DateTime.fromISO(createdAtStart, { setZone: true }) : undefined,
      endDate: createdAtEnd ? DateTime.fromISO(createdAtEnd, { setZone: true }) : undefined,
    };
  }, [createdAtStart, createdAtEnd]);

  if (productFeatureIsDisabled) {
    return (
      <DisabledProductFeature
        title='Inquiry Analytics'
        prompt='Understand insights about your data'
        icon={<IconInquiries />}
        helpLink={null}
        popupMessage='Your current plan does not have access to this feature.'
      />
    );
  }
  if (emptyData || !templateFunnelFlow || !template) {
    return (
      <Container>
        <LoadingIndicator fullscreen />
      </Container>
    );
  }

  const chartData = convertToChartData(templateFunnelConversionOverTime, isAbsolute);
  // Theoretically these two have the same steps, but during development they may diverge.
  const conversionOverTimeStepIdToName = buildConversionOverTimeIdToNameMapping(
    templateFunnelConversionOverTime,
  );

  const recommendedPeriod =
    lastQueryFilters.createdAtStart && lastQueryFilters.createdAtEnd
      ? getPeriod(lastQueryFilters.createdAtStart, lastQueryFilters.createdAtEnd)
      : Period.Day;

  const interval = getEffectiveInterval(recommendedPeriod, funnelPeriod);

  const xFormatter =
    funnelPeriod === Period.Hour && recommendedPeriod === funnelPeriod
      ? formatChartDateHourChart
      : funnelPeriod === Period.Month
        ? formatChartMonthName
        : formatChartDate;

  const inquiryTemplateId = template.id;
  const inquiryTemplateVersionId = templateFunnelFlow.token;
  return (
    <>
      <InactiveEnvironmentBanner />
      <Backdrop>
        <Section>
          <HeaderContainer>
            <Container>
              <Header>
                <NavBackContainer>
                  <NavBack to='/dashboard/inquiry-analytics/funnel'>
                    {template.attributes['name-display']}
                  </NavBack>
                </NavBackContainer>
              </Header>
            </Container>
          </HeaderContainer>
          <SoloColumn>
            {loading && <LoadingOverlay />}
            <Card>
              <SearchAndFilterCardBody>
                <SearchAndFilterContainer>
                  <DateContainer>
                    <AnalyticsDatePicker
                      dataTest='metrics-funnel-detailed-view__date-range'
                      handleDatesChangeV2={handleDateUpdateV2}
                      dateRange={dateRange}
                      disabled={loading}
                    />
                  </DateContainer>
                  <StepContainer>
                    <Tooltip
                      trigger='hover'
                      tooltip={
                        <TooltipContainer>
                          View metrics up until the selected step. If the end of the branch is
                          selected, the metrics will go for all steps.
                        </TooltipContainer>
                      }
                      hideArrow={false}
                    >
                      <StyledLabel>Steps</StyledLabel>
                    </Tooltip>
                    <StyledSelect
                      id='checklist-item-input__select-required-for-resolutions'
                      name='checklistItemRequiredForResolutions'
                      onChange={setStepId}
                      value={stepId}
                      preserveOrder={true}
                      options={stepIds.map((stepId) => {
                        return {
                          label: conversionOverTimeStepIdToName[stepId],
                          value: stepId,
                        };
                      })}
                      portal={true}
                      isMulti={false}
                      isSearchable={false}
                      isClearable={false}
                      size='sm'
                    />
                  </StepContainer>
                  <ConversionContainer>
                    <StyledLabel data-test='inquiry-funnel-detail-conversion-rate'>
                      Funnel metric
                    </StyledLabel>
                    <StyledSelect
                      id='checklist-item-input__select-required-for-resolutions'
                      name='checklistItemRequiredForResolutions'
                      onChange={(val: string) => {
                        setIsAbsolute(val === 'absolute');
                      }}
                      value={
                        isAbsolute
                          ? ABSOLUTE_RELATIVE_OPTIONS[0].value
                          : ABSOLUTE_RELATIVE_OPTIONS[1].value
                      }
                      options={ABSOLUTE_RELATIVE_OPTIONS}
                      portal={true}
                      isMulti={false}
                      isSearchable={false}
                      isClearable={false}
                      size='sm'
                    />
                  </ConversionContainer>
                  <RunContainer>
                    <RunButton
                      id='metrics-funnel-detailed-view__run'
                      data-test='metrics-funnel-detailed-view__run'
                      onClick={() =>
                        inquiryTemplateVersionId
                          ? loadMetrics({ inquiryTemplateId, inquiryTemplateVersionId })
                          : undefined
                      }
                      isPending={loading}
                      disabled={loading}
                      size='sm'
                    >
                      Run
                    </RunButton>
                  </RunContainer>
                </SearchAndFilterContainer>
              </SearchAndFilterCardBody>
            </Card>
            {renderFunnelFlow(templateFunnelFlow, filteredStepIds, isAbsolute, undefined)}
            <Card>
              <Card.Body>
                <HeaderContainer>
                  <Tooltip
                    placement='top'
                    trigger='hover'
                    tooltip={
                      <TooltipContainer>
                        View funnel metrics for each step over time to observe any anomalies.
                      </TooltipContainer>
                    }
                    hideArrow={false}
                  >
                    <Card.Title data-test='inquiry-funnel-conversion-over-time'>
                      Funnel metrics over time
                    </Card.Title>
                  </Tooltip>
                  {lastQueryFilters.createdAtStart && lastQueryFilters.createdAtEnd && (
                    <FunnelGranularitySelector
                      value={funnelPeriod}
                      handleUpdateGranularity={handleUpdateGranularity}
                      dateRange={[lastQueryFilters.createdAtStart, lastQueryFilters.createdAtEnd]}
                    />
                  )}
                </HeaderContainer>
                <FunnelConversionOverTimeLineChart
                  data={chartData.chartData}
                  percentagesData={chartData.percentagesData}
                  stepIds={filteredStepIds}
                  interval={interval}
                  xFormatter={xFormatter}
                  domain={generateDomain(
                    [
                      // These are in MS, need to divide
                      lastQueryFilters.createdAtStart
                        ? Date.parse(lastQueryFilters.createdAtStart) / 1000
                        : undefined,
                      lastQueryFilters.createdAtEnd
                        ? Date.parse(lastQueryFilters.createdAtEnd) / 1000
                        : undefined,
                    ],
                    funnelPeriod,
                  )}
                  period={funnelPeriod}
                  seriesFormatter={(id: string) => conversionOverTimeStepIdToName[id]}
                />
              </Card.Body>
            </Card>
          </SoloColumn>
        </Section>
      </Backdrop>
    </>
  );
};

export default InquiryFunnelAnalyticsDetailedView;
